/**
 * CRM event publishing.
 *
 * Writes business-relevant events to the Supabase `crm_outbox` table, which
 * Salesforce pulls and upserts by external id (see docs/SALESFORCE_INTEGRATION.md).
 *
 * Fire-and-forget and a no-op unless Supabase is configured AND a user is signed
 * in. NEVER pass private content (journal/prayer text) — identity, intent, and
 * engagement signals only.
 *
 * Event types:
 *   signup, plan_changed   — emitted server-side by triggers (not here)
 *   subscribe_intent       — user expressed interest in a paid offering { offering }
 *   gift_intent            — user wants to gift the app to someone { offering: 'gift' }
 *   engagement             — meaningful usage milestone { kind }
 */
import { supabase, isSupabaseConfigured } from './supabaseClient';
import { getStorageUser } from './userStorage';

const CLIENT_EVENT_FIELDS = {
    subscribe_intent: ['offering'],
    gift_intent: ['offering'],
    engagement: ['kind']
};

export const emitCrmEvent = (eventType, payload = {}) => {
    if (!isSupabaseConfigured) return Promise.resolve({ skipped: true });
    const userId = getStorageUser();
    if (!userId) return Promise.resolve({ skipped: true });
    const allowedFields = CLIENT_EVENT_FIELDS[eventType];
    if (!allowedFields) return Promise.resolve({ skipped: true });
    const safePayload = Object.fromEntries(
        allowedFields
            .filter((field) => typeof payload[field] === 'string')
            .map((field) => [field, payload[field].slice(0, 80)])
    );

    return supabase
        .from('crm_outbox')
        .insert({ user_id: userId, event_type: eventType, payload: safePayload })
        .then(({ error }) => {
            if (error) console.warn('[crm] emit failed:', error.message);
            return { error };
        });
};

export default emitCrmEvent;
